import type { ZudokuConfig } from "zudoku";
import { Button } from "zudoku/ui/Button.js";
import { SyntaxHighlight } from "zudoku/ui/SyntaxHighlight";
import { createAuth0ClientCredentialsPlugin } from "./plugins/auth0-client-credentials-plugin";
import { googleAnalyticsPlugin } from "./plugins/google-analytics-plugin";

// zudoku.config.ts
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "zudoku/ui/Card";


const config: ZudokuConfig = {
  //basePath: "/developer",
  site: {
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "ChannelSeal Logo",
      width: "130px",
    },
  footer: {
    position: "start",
    columns: [
      {
        title: "Product",
        position: "start", // position in grid, optional: start, center, end
        links: [
          { label: "User Portal", href: "https://staging.channelseal.com" },
        ],
      },
      {
        title: "Resources",
        position: "center",
        links: [
          { label: "Integrations", href: "https://github.com/channelseal/integrations" },
          { label: "Blog", href: "https://www.channelseal.com/blog"},
        ]
      },
      {
        title: "Company",
        position: "end",
        links: [
          { label: "Home", icon: "globe", href: "https://www.channelseal.com" },
          { label: "LinkedIn", href: "https://www.linkedin.com/company/channelseal" },
          { label: "Privacy Policy", href: "https://www.channelseal.com/privacy.html" },
        ],
      },
    ],
    copyright: `© ${new Date().getFullYear()} ChannelSeal. All rights reserved.`,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "ChannelSeal Logo",
      width: "100px"
    }
  },
  },
  theme: {
  // Custom fonts
      fonts: {
        sans: "Inter",
        mono: "JetBrains Mono",
      }
  },
  mdx: {
    components: {
      Card,
      CardContent,
      CardDescription,
      CardFooter,
      CardHeader,
      CardTitle,
    },
  },
  metadata: {
    "title": "ChannelSeal Developer Portal",
    "description": "Developer portal for ChannelSeal APIs and MCP Servers",
    "logo": "https://www.channelseal.com/images/channelseal_logo_transparent.png",
    "favicon": "https://www.channelseal.com/images/favicon-32x32.png",
    "generator": "Website Generator 1.0",
    "applicationName": "ChannelSeal Developer Portal",
    "referrer": "no-referrer",
    "keywords": ["api", "integration", "dlp", "api security"],
  },
  navigation: [
    {
      type: "category",
      label: "API Reference",
      icon: "book",
      items: [
        {
          type: "category",
          label: "API Reference",
          icon: "list-start",
          items: [
            {
              type: "doc",
              icon: "signpost",
              label: "Introduction",
              file: "/api-reference",
            },
            {
              type: "link",
              icon: "layout-list",
//               badge: {
//                 label: "New",
//                 color: "blue",
//               },
              label: "API Discovery Service API",
              to: "/api-discovery",
            },
            {
              type: "link",
              icon: "cloud-upload",
//               badge: {
//                 label: "New",
//                 color: "blue",
//               },
              label: "API Catalog API",
              to: "/catalog",
            },
            {
              type: "link",
              icon: "shapes",
//               badge: {
//                 label: "New",
//                 color: "blue",
//               },
              label: "Data Classification API",
              to: "/data-classification",
            },
            {
              type: "link",
              icon: "microchip",
//               badge: {
//                 label: "New",
//                 color: "blue",
//               },
              label: "Platform API",
              to: "/platform",
            },
            {
              type: "link",
              icon: "blocks",
//               badge: {
//                 label: "New",
//                 color: "blue",
//               },
              label: "Schemas",
              to: "/components",
            },
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Agent Skills",
      icon: "square-terminal",
      items: [
        {
          type: "category",
          label: "Agent Skills",
          icon: "list-start",
          items: [
              {
                type: "doc",
                icon: "bot",
                label: "Introduction",
                file: "/agent-skills",
             },
             {
                type: "doc",
                icon: "workflow",
                label: "Process Skills",
                file: "/skills/process-skills/introduction",
             },
             {
                type: "doc",
                icon: "boxes",
                label: "Resource Skills",
                file: "/skills/resource-skills/introduction",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      to: "https://github.com/channelseal/integrations",
      label: "Integrations",
      icon: "blocks",
    },
  ],
  redirects: [{ from: "/", to: "/api-reference" }],
  catalogs: {
    path: "/apis",
    label: "APIs",
  },
  apis: [
    {
      type: "file",
      input: "./apis/api-discovery-specs.yaml",
      path: "/api-discovery",
      categories: [{ label: "General", tags: ["Operational"] }],
      options: {
        disablePlayground: false, // Disable the interactive API playground
        disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        schemaDownload: { enabled: true }, // Enable schema download button
      },
    },
    {
      type: "file",
      input: "./apis/data-classification-specs.yaml",
      path: "/data-classification",
      categories: [{ label: "General", tags: ["Operational"] }],
      options: {
        disablePlayground: false, // Disable the interactive API playground
        disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        schemaDownload: { enabled: true }, // Enable schema download button
      },
    },
    {
      type: "file",
      input: "./apis/platform-api-specs.yaml",
      path: "/platform",
      categories: [{ label: "General", tags: ["Operational"] }],
      options: {
        disablePlayground: false, // Disable the interactive API playground
        disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        schemaDownload: { enabled: true }, // Enable schema download button
      },
    },
    {
      type: "file",
      input: "./apis/catalog-api-specs.yaml",
      path: "/catalog",
      categories: [{ label: "General", tags: ["Operational"] }],
      options: {
        disablePlayground: false, // Disable the interactive API playground
        disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        schemaDownload: { enabled: true }, // Enable schema download button
      },
    },
    {
      type: "file",
      input: "./apis/components.yaml",
      path: "/components",
      categories: [{ label: "General", tags: ["Operational"] }],
      options: {
        disablePlayground: false, // Disable the interactive API playground
        disableSidecar: false, // Disable the sidecar completely
        showVersionSelect: "if-available", // Control version selector visibility
        schemaDownload: { enabled: true }, // Enable schema download button
      },
    },
  ],
  // User authentication
  authentication: {
    type: "auth0",
    domain: process.env.ZUDOKU_PUBLIC_OKTA_DOMAIN,
    clientId: process.env.ZUDOKU_PUBLIC_OAUTH_CLIENT_ID,
    scopes: ["openid", "profile", "email", "custom_scope"],
    audience: process.env.ZUDOKU_PUBLIC_JWT_AUDIENCE,
  },
  protectedRoutes:
  [
    "/components/*",
    "/platform/*",
    "/catalog/*",
    "/api-discovery/*",
    "/data-classification/*",
    "/skills/*",
  ],
  // API Identity plugin for M2M authentication in playground
  plugins: [
    createAuth0ClientCredentialsPlugin({
      tokenEndpoint: "/api/auth/token",
    }),
    googleAnalyticsPlugin({
      measurementId: process.env.ZUDOKU_PUBLIC_GA_MEASUREMENT_ID ?? "G-0HBD1BK5R3",
    }),
  ],
  docs: {
    files: "/pages/**/*.{md,mdx}",
    publishMarkdown: true,
    defaultOptions: {
      copyPage: true, // Enable copy button for all pages
    }
  },
  search: {
    type: "pagefind",
    // Optional: Maximum number of sub results per page
    maxSubResults: 3,
    // Optional: Configure search result ranking (defaults shown below)
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  slots: {
    "head-navigation-end": () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" asChild>
          <a href="https://www.channelseal.com">
             Home
          </a>
        </Button>
        <Button variant="ghost" asChild>
          <a href="https://www.channelseal.com">
             Early Access
          </a>
        </Button>
      </div>
    ),
  },
  port: 5001,
};
export default config;
